import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Heart } from 'lucide-react';
import { sendPrayerHeart, prayerStreamUrl } from '../lib/api';

/**
 * FloatingHearts — Phase 1 of the live floating-hearts feature.
 *
 * Renders a small heart button and a transient stack of hearts that
 * float up-and-out whenever ANY current watcher of this prayer taps
 * their heart button. Every tap POSTs to `/prayers/{id}/heart`, which
 * the backend fans out via SSE to every open `/prayers/{id}/stream`
 * subscriber — so a viewer sees hearts from other watchers in near
 * real-time as well as their own optimistic hearts.
 *
 * Design notes:
 *  - Auth: sending requires sign-in (backend rejects otherwise); the
 *    SSE stream is open to anonymous viewers too so signed-out folks
 *    can still SEE hearts even if they can't SEND.
 *  - Rate limit: server-side is 1 heart / user / 3s. The button
 *    disables locally for the same window so spam-tappers get instant
 *    feedback instead of silent 200-throttled responses.
 *  - Perf: hearts self-cull after their animation ends (2.4s) so the
 *    array never grows unboundedly. When the tab is hidden we pause
 *    subscribing to save battery.
 *  - Fan-out cap: the backend rejects the SSE handshake with 429 once
 *    a prayer has too many concurrent watchers. We surface that as a
 *    silent poll-only mode — the send button still works, viewers
 *    still see their own hearts, they just don't see other watchers'.
 */

// Longest visual life of a single heart (must match the CSS keyframe
// duration below). Used to schedule the removal timer.
const HEART_LIFE_MS = 2400;

// Local throttle window — mirrors the server's HEART_MIN_INTERVAL so
// the disabled state clears at the same moment the server accepts the
// next heart.
const LOCAL_THROTTLE_MS = 3000;

let _heartIdSeq = 0;

const FloatingHearts = ({ prayerId, canSend = true, hideSendButton = false, onIncomingHeart, onCandleUpdate }) => {
  const [hearts, setHearts] = useState([]);
  const [sending, setSending] = useState(false);
  const lastSentRef = useRef(0);
  const streamRef = useRef(null);
  const cullTimersRef = useRef(new Map());
  // Stable ref for the incoming-heart callback so the SSE useEffect
  // doesn't re-subscribe every time the parent passes a new closure.
  const onIncomingHeartRef = useRef(onIncomingHeart);
  useEffect(() => { onIncomingHeartRef.current = onIncomingHeart; }, [onIncomingHeart]);
  // Same pattern for candle updates — stable ref so SSE useEffect
  // doesn't tear down and re-subscribe on every parent re-render.
  const onCandleUpdateRef = useRef(onCandleUpdate);
  useEffect(() => { onCandleUpdateRef.current = onCandleUpdate; }, [onCandleUpdate]);

  // Push a new floating heart into the local render list. Each heart
  // is tagged with a random horizontal offset + drift so ten hearts
  // arriving in the same tick don't stack on top of each other.
  const pushHeart = useCallback(() => {
    const id = ++_heartIdSeq;
    // Offsets are computed once at spawn time and interpolated via
    // CSS custom properties — no per-frame JS work.
    const startX = Math.random() * 40 - 20; // -20..+20 px
    const driftX = Math.random() * 60 - 30; // -30..+30 px
    const scale = 0.85 + Math.random() * 0.4; // 0.85..1.25
    setHearts((prev) => [...prev, { id, startX, driftX, scale }]);
    const timer = setTimeout(() => {
      setHearts((prev) => prev.filter((h) => h.id !== id));
      cullTimersRef.current.delete(id);
    }, HEART_LIFE_MS);
    cullTimersRef.current.set(id, timer);
  }, []);

  // Subscribe to the per-prayer SSE stream. Reconnects on error with a
  // 3s backoff; skips subscription when the tab is hidden to save
  // resources on background tabs.
  useEffect(() => {
    if (!prayerId) return undefined;
    let cancelled = false;
    let retryTimer = null;

    const connect = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        // Try again once the tab is visible instead of eagerly opening
        // a stream nobody's looking at.
        return;
      }
      try {
        const es = new EventSource(prayerStreamUrl(prayerId), { withCredentials: true });
        streamRef.current = es;
        es.onmessage = (evt) => {
          if (!evt.data) return;
          try {
            const payload = JSON.parse(evt.data);
            if (payload?.type === 'heart') {
              pushHeart();
              // Notify the parent so it can render hearts elsewhere
              // (e.g. inside the immersive overlay in WatchPrayer,
              // which sits above this component's stage).
              try { onIncomingHeartRef.current?.(payload); } catch (err) { console.warn('onIncomingHeart cb threw', err); }
            } else if (payload?.type === 'candle') {
              // Candle lights/unlights broadcast the fresh
              // `candles_count`. Route it to the parent so the
              // immersive overlay counter (and any other consumer)
              // can update without polling.
              try { onCandleUpdateRef.current?.(payload); } catch (err) { console.warn('onCandleUpdate cb threw', err); }
            }
          } catch {
            /* ignore malformed */
          }
        };
        es.onerror = () => {
          try { es.close(); } catch { /* noop */ }
          if (streamRef.current === es) streamRef.current = null;
          if (cancelled) return;
          // Backoff then retry — covers transient network hiccups
          // and the server-side 429 cap (in which case retry is
          // still cheap; the client just falls back to poll-only
          // for that session).
          retryTimer = setTimeout(connect, 3000);
        };
      } catch (err) {
        // Browser without EventSource support (rare in modern
        // clients). Fail silently — the send button still works.
        console.warn('[FloatingHearts] EventSource setup failed', err);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !streamRef.current) {
        connect();
      } else if (document.visibilityState === 'hidden' && streamRef.current) {
        try { streamRef.current.close(); } catch { /* noop */ }
        streamRef.current = null;
      }
    };

    connect();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (streamRef.current) {
        try { streamRef.current.close(); } catch { /* noop */ }
        streamRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [prayerId, pushHeart]);

  // Cull all pending timers on unmount so we never call setState after
  // the component is gone.
  useEffect(() => () => {
    cullTimersRef.current.forEach((t) => clearTimeout(t));
    cullTimersRef.current.clear();
  }, []);

  const handleTap = async () => {
    if (!canSend || sending) return;
    const now = Date.now();
    if (now - lastSentRef.current < LOCAL_THROTTLE_MS) return;
    lastSentRef.current = now;
    setSending(true);
    // Optimistic local heart — user sees their tap instantly even if
    // the network is slow. SSE will fan the same event back to other
    // watchers.
    pushHeart();
    try {
      await sendPrayerHeart(prayerId);
    } catch (err) {
      // 401 = signed out mid-session; 429 = extreme abuse guard on
      // the backend. Neither case needs a visible error — the local
      // heart already flew, and the client will re-attempt on the
      // next tap.
      if (err?.response?.status && ![401, 429].includes(err.response.status)) {
        console.warn('[FloatingHearts] send failed', err);
      }
    } finally {
      // Match the server's throttle window before re-enabling.
      setTimeout(() => setSending(false), LOCAL_THROTTLE_MS);
    }
  };

  return (
    <div className="relative inline-flex" data-testid="floating-hearts-root">
      {!hideSendButton && (
        <button
          type="button"
          onClick={handleTap}
          disabled={!canSend || sending}
          aria-label={canSend ? 'Send a heart' : 'Sign in to send hearts'}
          title={canSend ? 'Send a heart' : 'Sign in to send hearts'}
          data-testid="send-heart-btn"
          className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
            canSend
              ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-60'
              : 'text-zinc-400 cursor-not-allowed'
          }`}
        >
          <Heart size={22} strokeWidth={2.25} className={sending ? 'animate-ping-once' : ''} />
        </button>
      )}
      {/* Overlay for the floating hearts. `pointer-events-none` so it
          never steals clicks from surrounding controls. Positioned
          absolute above the button so hearts appear to erupt from it. */}
      <div
        className="pointer-events-none absolute left-1/2 bottom-full -translate-x-1/2 h-40 w-40 overflow-visible"
        data-testid="floating-hearts-stage"
      >
        {hearts.map((h) => (
          <span
            key={h.id}
            className="absolute left-1/2 bottom-0 -translate-x-1/2 text-rose-500"
            style={{
              '--start-x': `${h.startX}px`,
              '--drift-x': `${h.driftX}px`,
              '--scale': h.scale,
              animation: `pnt-heart-rise ${HEART_LIFE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards`,
            }}
            aria-hidden="true"
          >
            <Heart size={18} fill="currentColor" strokeWidth={0} />
          </span>
        ))}
      </div>
      {/* Local keyframes — scoped to this component so we don't need a
          global stylesheet touch. Two custom properties (startX, driftX)
          control per-heart horizontal jitter so simultaneous hearts
          fan out instead of stacking. */}
      <style>{`
        @keyframes pnt-heart-rise {
          0%   { transform: translate(calc(-50% + var(--start-x)), 0) scale(var(--scale, 1)); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate(calc(-50% + var(--drift-x)), -120px) scale(var(--scale, 1)); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default FloatingHearts;
